import styled from 'styled-components';
import {
  EquipmentActions, EquipmentItemRow, FormButtonGroup, FormLabel, FormPanel, FormPanelHeader, FormPanelIcon, FormPanelTitle,
  FormValue, SummaryRow, SummaryTable,
} from '../calendar.styles';

// Podgląd rezerwacji (tryb bez edycji) w ReservationFormView.js — przebudowa czytelności 2026-10-02.
//
// Okno odpowiada na pytanie „w jakim stanie jest to wypożyczenie?": kto, kiedy, co, ile zostało
// do zapłaty, czy sprzęt wydany. Dawniej podgląd był formularzem z wyszarzonymi polami
// (dane w ramkach jak do wpisywania), a najważniejsze fakty były rozrzucone na ~3000 px.
// Teraz: nagłówek z klientem i terminem, po lewej to, co fizyczne (sprzęt, wydanie, zdjęcia),
// po prawej pieniądze, a akcje w pasku przyklejonym do dołu okna.

export const DetailsHero = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 24px;
  /* miejsce na przycisk × w prawym górnym rogu okna; padding, nie margines, żeby linia
     pod nagłówkiem dochodziła do prawej krawędzi treści */
  padding: 0 40px 20px 0;
  border-bottom: 1px solid var(--input-border);
`;

export const DetailsEyebrow = styled.p`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

export const DetailsTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 14px;
`;

export const DetailsTitle = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--text);

  @media (max-width: 768px) { font-size: 1.4rem; }
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${p => p.$fg || 'var(--text-muted)'};
  background: ${p => p.$bg || 'var(--hover-bg)'};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const DetailsWhen = styled.p`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text);

  > span { font-weight: 500; color: var(--text-muted); }
`;

export const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

export const ContactChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 6px 12px;
  border: 1px solid var(--input-border);
  border-radius: 999px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.875rem;
  text-decoration: none;
  overflow-wrap: anywhere;

  svg { flex-shrink: 0; width: 16px; height: 16px; color: var(--text-muted); }
  &[href]:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

export const DetailsGrid = styled.div`
  display: grid;
  gap: 20px;
  align-items: start;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.35fr) minmax(300px, 1fr);
  }
`;

export const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  /* Panele przeniesione bez zmian (zdjęcia stanu sprzętu) mają własny margines dolny. */
  > * { margin-bottom: 0 !important; }

  /* Jedna kolumna: karty obu kolumn stają się elementami siatki, żeby rozliczenie (MoneyCard,
     order: -1) mogło stanąć zaraz pod nagłówkiem, a nie pod całą dokumentacją zdjęć. */
  @media (max-width: 899px) { display: contents; }

  /* Panel zdjęć jest wspólny z trybem edycji (FormPanel); w podglądzie wygląda jak reszta kart. */
  > ${FormPanel} {
    padding: 18px 20px;
  }

  > ${FormPanel} ${FormPanelHeader} {
    margin-bottom: 14px;
    padding-bottom: 0;
    border-bottom: none;
  }

  > ${FormPanel} ${FormPanelIcon} { display: none; }

  > ${FormPanel} ${FormPanelTitle} {
    font-size: 1rem;
    font-weight: 700;
  }
`;

export const DetailsCard = styled.section`
  min-width: 0;
  padding: 18px 20px;
  border: 1px solid var(--input-border);
  border-radius: 16px;
  background: var(--panel-bg);

  /* W podglądzie wartości są tekstem, nie polem formularza: bez ramki i tła „do wpisania". */
  ${FormValue} {
    min-height: 0;
    padding: 0;
    border: none;
    background: none;
    font-weight: 600;
  }

  ${FormLabel} {
    margin-bottom: 4px;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-muted);

    svg { display: none; }
  }

  /* Wąsko: cena pod nazwą sztuki, zamiast ściskać nazwę do kilku liter w wierszu. */
  @media (max-width: 520px) {
    ${EquipmentItemRow} { flex-wrap: wrap; gap: 6px; }
    ${EquipmentActions} { margin-left: 0; }
  }
`;

export const DetailsCardHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

export const DetailsCardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
`;

export const DetailsCount = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
`;

export const PaymentGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  & > * + * {
    padding-top: 18px;
    border-top: 1px solid var(--input-border);
  }
`;

export const NoteText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.55;
  color: var(--text);
`;

// Rozliczenie: kwota do zapłaty jest najważniejszą liczbą przy ladzie, więc stoi na górze
// prawej kolumny, a tabela pod nią ją objaśnia.
export const MoneyCard = styled(DetailsCard)`
  background: var(--card-bg);
  border-width: 2px;
  border-color: ${p => (p.$paid ? 'var(--success-border, var(--success))' : 'var(--input-border)')};

  @media (max-width: 899px) { order: -1; }

  ${SummaryTable} {
    margin-top: 16px;
    background: transparent;
  }

  ${SummaryRow} {
    padding: 10px 14px;
  }

  /* W tabeli podglądu nie ma wiersza „Pozostało do zapłaty" (stoi wyżej, dużą liczbą), więc
     ostatni wiersz to zwykły wiersz, nie podsumowanie. */
  ${SummaryRow}:last-child {
    background: none;
    font-weight: inherit;
  }
`;

export const MoneyLabel = styled.p`
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

export const MoneyAmount = styled.p`
  margin: 4px 0 0;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: ${p => (p.$paid ? 'var(--success)' : 'var(--text)')};
`;

export const MoneyNote = styled.p`
  margin: 6px 0 0;
  font-size: 0.85rem;
  color: var(--text-muted);
`;

// Pasek akcji przyklejony do dołu przewijanego okna: „Edytuj", umowa i faktura są zawsze pod
// ręką, a nie dopiero po przewinięciu całego okna. Ujemny `bottom` = dolny padding okna
// (FormContainer: 32 px, na telefonie 20 px), żeby pasek dochodził do krawędzi.
export const StickyActions = styled(FormButtonGroup)`
  position: sticky;
  bottom: -32px;
  /* Ponad polami formularza edycji (przycisk jednostki rabatu ma z-index --z-dropdown), ale
     pod otwartymi listami i kalendarzykiem dat (--z-popover), żeby ich nie przycinał. */
  z-index: calc(var(--z-dropdown) + 1);
  margin: 24px -32px -32px;
  padding: 16px 32px;
  background: var(--card-bg);
  border-top: 1px solid var(--input-border);
  border-radius: 0 0 20px 20px;
  flex-wrap: wrap;

  .spacer { flex: 1 1 auto; }

  /* Telefon: główna akcja na całą szerokość, pozostałe po dwie w rzędzie. Trzy przyciski jeden
     pod drugim zajmowały prawie ćwierć ekranu, zasłaniając treść, do której pasek prowadzi. */
  @media (max-width: 768px) {
    bottom: -20px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 20px -20px -20px;
    padding: 12px 20px;

    .spacer { display: none; }
    > button { width: 100%; min-width: 0; padding-left: 8px; padding-right: 8px; }
    > button:last-child { grid-column: 1 / -1; order: -1; }
  }
`;
